import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { Section } from '../ui/Section';
import { Button } from '../ui/Button';
import { Quote, Star, MessageSquarePlus, X, CheckCircle2, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useTestimonials, addTestimonial } from '../../hooks/useTestimonials';
const SUBMIT_API = 'https://contentful-bridge-75k0bmv20-belo-digitals-projects.vercel.app/api/submit';
function toTitleCase(str: string): string {
  return str.replace(/\b\w/g, char => char.toUpperCase());
}
export function TestimonialsSection() {
  const {
    testimonials,
    loading
  } = useTestimonials();
  const [current, setCurrent] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'publishing' | 'success'>('idle');
  const [direction, setDirection] = useState(1);
  const [form, setForm] = useState({
    name: '',
    titleRole: '',
    company: '',
    quote: '',
    rating: 0
  });
  const [ratingError, setRatingError] = useState(false);
  // Clamp current index if testimonials change
  useEffect(() => {
    if (testimonials.length > 0 && current >= testimonials.length) {
      setCurrent(testimonials.length - 1);
    }
  }, [testimonials.length, current]);
  // Auto-rotate
  useEffect(() => {
    if (showForm || testimonials.length <= 1) return;
    const timer = setInterval(() => {
      setDirection(1);
      setCurrent(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length, showForm]);
  const goNext = () => {
    setDirection(1);
    setCurrent(prev => (prev + 1) % testimonials.length);
  };
  const goPrev = () => {
    setDirection(-1);
    setCurrent(prev => (prev - 1 + testimonials.length) % testimonials.length);
  };
  const goTo = (index: number) => {
    setDirection(index > current ? 1 : -1);
    setCurrent(index);
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.rating === 0) {
      setRatingError(true);
      return;
    }
    setRatingError(false);
    setFormStatus('publishing');
    try {
      const payload = {
        contentType: 'testimonials',
        name: form.name,
        titlerole: form.titleRole,
        testimonial: form.quote,
        rating: form.rating,
        companyOrganization: form.company
      };
      console.log('[Testimonial] Submitting to:', SUBMIT_API, payload);
      const res = await fetch(SUBMIT_API, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const responseText = await res.text();
      console.log('[Testimonial] Response:', res.status, responseText);
      if (!res.ok) {
        throw new Error(`API returned ${res.status}: ${responseText}`);
      }
      // Save locally for immediate display
      await addTestimonial({
        name: form.name,
        company: [form.titleRole, form.company].filter(Boolean).join(', '),
        quote: form.quote,
        rating: form.rating
      });
      toast.success('Thank you! Your testimonial has been submitted.');
      setFormStatus('success');
      setTimeout(() => {
        setFormStatus('idle');
        setShowForm(false);
        setForm({
          name: '',
          titleRole: '',
          company: '',
          quote: '',
          rating: 0
        });
      }, 3000);
    } catch (err: any) {
      console.error('[Testimonial] Submission failed:', err);
      setFormStatus('idle');
      toast.error(err?.message || 'Failed to submit. Please try again.');
    }
  };
  if (loading) {
    return <Section id="testimonials" background="white" data-id="element-358">
        <div className="flex items-center justify-center py-20 text-gray-400" data-id="element-359">
          <Loader2 className="w-5 h-5 animate-spin mr-2" data-id="element-360" />
          Loading testimonials…
        </div>
      </Section>;
  }
  if (testimonials.length === 0) return null;
  const safeIndex = Math.min(current, testimonials.length - 1);
  return <Section id="testimonials" background="white" data-id="element-361">
      <div className="max-w-4xl mx-auto text-center" data-id="element-362">
        <div className="mb-8 flex justify-center" data-id="element-363">
          <div className="w-12 h-12 bg-[#00316B]/10 rounded-full flex items-center justify-center text-[#00316B]" data-id="element-364">
            <Quote size={24} data-id="element-365" />
          </div>
        </div>

        <div className="relative min-h-[240px] flex flex-col items-center justify-center" data-id="element-366">
          {/* Left Arrow */}
          {testimonials.length > 1 && <button onClick={goPrev} className="absolute left-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors z-10" aria-label="Previous testimonial" data-id="element-367">
              <ChevronLeft className="w-5 h-5" data-id="element-368" />
            </button>}

          {/* Right Arrow */}
          {testimonials.length > 1 && <button onClick={goNext} className="absolute right-0 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center text-gray-600 transition-colors z-10" aria-label="Next testimonial" data-id="element-369">
              <ChevronRight className="w-5 h-5" data-id="element-370" />
            </button>}

          <div className="px-14" data-id="element-371">
            <AnimatePresence mode="wait" custom={direction} data-id="element-372">
              <motion.div key={safeIndex} custom={direction} initial={{
              opacity: 0,
              x: direction * 40
            }} animate={{
              opacity: 1,
              x: 0
            }} exit={{
              opacity: 0,
              x: direction * -40
            }} transition={{
              duration: 0.35,
              ease: 'easeInOut'
            }} className="flex flex-col items-center" data-id="element-373">
                {/* Star Rating */}
                <div className="flex gap-1 mb-6" data-id="element-374">
                  {Array.from({
                  length: 5
                }).map((_, i) => <Star key={i} className={`w-5 h-5 ${i < testimonials[safeIndex].rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-375" />)}
                </div>

                <blockquote className="text-2xl md:text-3xl font-heading font-medium text-[#0C0C0C] leading-tight mb-8" data-id="element-376">
                  "{testimonials[safeIndex].quote}"
                </blockquote>

                <div className="font-bold text-[#0C0C0C] text-lg" data-id="element-377">
                  {testimonials[safeIndex].name}
                </div>
                <div className="text-sm text-gray-500 mt-1 font-medium" data-id="element-378">
                  {testimonials[safeIndex].company}
                </div>
                {testimonials[safeIndex].verified && <div className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#00316B] font-medium" data-id="element-379">
                    <img src="https://cdn.magicpatterns.com/uploads/9mmk6QtVmSzZGQfVenZb4U/badge.svg" alt="Verified" className="w-4 h-4" data-id="element-380" />
                    Verified Testimonial
                  </div>}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Dot indicators */}
        <div className="flex justify-center gap-2 mt-10 mb-12" data-id="element-381">
          {testimonials.map((_, index) => <button key={index} onClick={() => goTo(index)} className={`w-2 h-2 rounded-full transition-all duration-300 ${index === safeIndex ? 'bg-[#00316B] w-6' : 'bg-gray-300 hover:bg-gray-400'}`} aria-label={`Go to testimonial ${index + 1}`} data-id="element-382" />)}
        </div>

        {/* Public Submission Button / Form */}
        <div className="mt-8" data-id="element-383">
          <AnimatePresence mode="wait" data-id="element-384">
            {!showForm ? <motion.div initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={{
            opacity: 0,
            y: -10
          }} data-id="element-385">
                <Button variant="outline" onClick={() => setShowForm(true)} leftIcon={<MessageSquarePlus className="w-4 h-4" data-id="element-387" />} data-id="element-386">
                  Write a Testimonial
                </Button>
              </motion.div> : <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} exit={{
            opacity: 0,
            height: 0
          }} className="max-w-lg mx-auto bg-gray-50 rounded-2xl border border-gray-200 p-6 text-left relative overflow-hidden" data-id="element-388">
                <button onClick={() => setShowForm(false)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600" data-id="element-389">
                  <X className="w-5 h-5" data-id="element-390" />
                </button>

                {formStatus === 'success' ? <div className="flex flex-col items-center justify-center py-10 text-center" data-id="element-391">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4" data-id="element-392">
                      <CheckCircle2 className="w-6 h-6 text-green-600" data-id="element-393" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-394">
                      Thank You!
                    </h3>
                    <p className="text-gray-600" data-id="element-395">
                      Your testimonial has been submitted.
                    </p>
                  </div> : <>
                    <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-396">
                      Share Your Experience
                    </h3>
                    <form onSubmit={handleSubmit} className="space-y-4" data-id="element-397">
                      <div data-id="element-398">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-399">
                          Your Name <span className="text-red-500" data-id="element-400">*</span>
                        </label>
                        <input type="text" required className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. John Doe" value={form.name} onChange={e => setForm({
                    ...form,
                    name: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-401" />
                      </div>

                      <div data-id="element-402">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-403">
                          Title / Role
                        </label>
                        <input type="text" className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. Head of Marketing" value={form.titleRole} onChange={e => setForm({
                    ...form,
                    titleRole: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-404" />
                      </div>

                      <div data-id="element-405">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-406">
                          Company / Organization{' '}
                          <span className="text-red-500" data-id="element-407">*</span>
                        </label>
                        <input type="text" required className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all" placeholder="e.g. Acme Corp" value={form.company} onChange={e => setForm({
                    ...form,
                    company: toTitleCase(e.target.value)
                  })} disabled={formStatus === 'publishing'} data-id="element-408" />
                      </div>

                      <div data-id="element-409">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-410">
                          Rating <span className="text-red-500" data-id="element-411">*</span>
                        </label>
                        <div className="flex gap-1" data-id="element-412">
                          {[1, 2, 3, 4, 5].map(star => <button key={star} type="button" onClick={() => {
                      setForm({
                        ...form,
                        rating: star
                      });
                      setRatingError(false);
                    }} className="focus:outline-none transition-transform active:scale-90" disabled={formStatus === 'publishing'} data-id="element-413">
                              <Star className={`w-6 h-6 ${star <= form.rating ? 'fill-[#F59E0B] text-[#F59E0B]' : 'text-gray-300'}`} data-id="element-414" />
                            </button>)}
                        </div>
                        {ratingError && <p className="text-xs text-red-500 mt-1" data-id="element-415">
                            Please select a rating
                          </p>}
                      </div>

                      <div data-id="element-416">
                        <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-417">
                          Testimonial <span className="text-red-500" data-id="element-418">*</span>
                        </label>
                        <textarea required rows={3} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#00316B] focus:border-transparent outline-none transition-all resize-none" placeholder="How was your experience working with us?" value={form.quote} onChange={e => setForm({
                    ...form,
                    quote: e.target.value
                  })} disabled={formStatus === 'publishing'} data-id="element-419" />
                      </div>

                      <div className="pt-2" data-id="element-420">
                        <Button type="submit" variant="secondary" className="w-full" isLoading={formStatus === 'publishing'} disabled={formStatus === 'publishing'} data-id="element-421">
                          {formStatus === 'publishing' ? 'Publishing…' : 'Submit Testimonial'}
                        </Button>
                      </div>
                    </form>
                  </>}
              </motion.div>}
          </AnimatePresence>
        </div>
      </div>
    </Section>;
}